<template>
  <div class="tw-flex-1">
    <FooterLabel>
      {{ I18n.t('adjustment.modal.attachment') }}
    </FooterLabel>
    <div class="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
      <DirectUploadDropzone
        v-if="!props.disabled"
        :multiple="true"
        :on-file-progress="updateUploadProgress"
        :on-file-added="startFileUpload"
        :on-file-uploaded="addAttachment"
      >
        <Paperclip :disabled="disabled" />
      </DirectUploadDropzone>
      <Paperclip v-else :disabled="disabled" />
      <SfDocumentPill
        v-for="attachment in attachments"
        :key="attachment.id"
        :allow-delete="!disabled"
        :file-size="attachment.size"
        :progress="uploadProgress[attachment.id]"
        @delete="() => removeAttachment(attachment.id)"
      >
        {{ attachment.filename }}
      </SfDocumentPill>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isExistingAttachment } from '../predicates'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import FooterLabel from './adjustments-modal-footer-label.vue'
import Paperclip from './adjustments-modal-document-upload-paperclip.vue'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'
import { AdjustmentDocument } from '../types'

interface Props {
  disabled?: boolean
}

const props = defineProps<Props>()

const I18n = useI18n()

const store = useAdjustmentsModalStore()

const { attachmentObjects, attachmentOrder, uploadProgress } =
  storeToRefs(store)
const {
  addAttachment,
  removeAttachment,
  startFileUpload,
  updateUploadProgress,
} = store

const attachments = computed(() =>
  attachmentOrder.value.reduce((acc, attachmentId) => {
    const current = attachmentObjects.value[attachmentId]

    if (!current) {
      return acc
    }

    if (isExistingAttachment(current)) {
      acc.push({
        ...current,
        size: 0,
      })
    } else {
      acc.push(current)
    }

    return acc
  }, [] as AdjustmentDocument[])
)
</script>
